import { useLayoutEffect, useRef, useState } from 'react';
import { Box, Button } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { COLORS } from './formTheme';

const LINES = 3;

// Question text. Only labels in a half-width field (two per row) get a "read more":
// when the text is longer than LINES lines it starts collapsed.
export default function QuestionLabel({
  children,
  component = 'span',
  sx,
  collapsible: canCollapse = false,
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [overflows, setOverflows] = useState(false);
  const textRef = useRef(null);

  const collapsible = canCollapse && typeof children === 'string';

  // only offer "read more" when the clamped text is really cut off
  useLayoutEffect(() => {
    const el = textRef.current;
    if (!collapsible || !el || open) return undefined;
    const measure = () => setOverflows(el.scrollHeight > el.clientHeight + 1);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [collapsible, open, children]);

  if (!children) return null;

  return (
    <Box component={component} sx={{ display: 'block', ...sx }}>
      <Box
        ref={textRef}
        component='span'
        sx={
          collapsible && !open
            ? {
                display: '-webkit-box',
                WebkitLineClamp: LINES,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
              }
            : undefined
        }
      >
        {children}
      </Box>
      {collapsible && (open || overflows) && (
        <>
          {' '}
          <Button
            type='button'
            size='small'
            onClick={() => setOpen((v) => !v)}
            aria-expanded={open}
            sx={{
              p: 0,
              mt: 0.5,
              minWidth: 0,
              textTransform: 'none',
              fontWeight: 600,
              color: COLORS.accent,
              textDecoration: 'underline',
            }}
          >
            {open ? t('read_less') : t('read_more')}
          </Button>
        </>
      )}
    </Box>
  );
}
